import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const MAX_ITEMS = 20;

function prependUnique(items, entry, getKey) {
  const key = getKey(entry);
  const filtered = items.filter((item) => getKey(item) !== key);
  return [entry, ...filtered].slice(0, MAX_ITEMS);
}

export default function useSearchHistory() {
  const { user } = useAuth();
  const userId = user?.id;
  const [domainHistory, setDomainHistory] = useState([]);
  const [contactHistory, setContactHistory] = useState([]);

  useEffect(() => {
    if (!userId) {
      setDomainHistory([]);
      setContactHistory([]);
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const result = await api.getSearchHistory();
        if (cancelled || !result?.success) return;
        setDomainHistory(Array.isArray(result.domains) ? result.domains : []);
        setContactHistory(Array.isArray(result.contacts) ? result.contacts : []);
      } catch (error) {
        if (!cancelled) {
          logger.warn('Failed to load search history from server', error);
          setDomainHistory([]);
          setContactHistory([]);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const addDomainSearch = useCallback(async (record) => {
    if (!record || !userId) return;

    const domain = (record.Domain || record.domain || '').toLowerCase().trim();
    if (!domain) return;

    try {
      const result = await api.addDomainSearchHistory(record);
      if (result?.success && result.entry) {
        setDomainHistory((prev) => prependUnique(prev, result.entry, (item) => item.domain));
      }
    } catch (error) {
      logger.warn('Failed to save domain search history', error);
    }
  }, [userId]);

  const addContactSearch = useCallback(async (apiResponse) => {
    if (!apiResponse?.data || !userId) return;

    try {
      const result = await api.addContactSearchHistory(apiResponse);
      if (result?.success && result.entry) {
        setContactHistory((prev) => prependUnique(prev, result.entry, (item) => item.id));
      }
    } catch (error) {
      logger.warn('Failed to save contact search history', error);
    }
  }, [userId]);

  const removeDomain = useCallback(async (id) => {
    if (!userId) return;

    setDomainHistory((prev) => prev.filter((item) => item.id !== id));
    try {
      await api.deleteSearchHistoryEntry(id);
    } catch (error) {
      logger.warn('Failed to remove domain search history entry', error);
    }
  }, [userId]);

  const removeContact = useCallback(async (id) => {
    if (!userId) return;

    setContactHistory((prev) => prev.filter((item) => item.id !== id));
    try {
      await api.deleteSearchHistoryEntry(id);
    } catch (error) {
      logger.warn('Failed to remove contact search history entry', error);
    }
  }, [userId]);

  const clearDomainHistory = useCallback(async () => {
    if (!userId) return;

    setDomainHistory([]);
    try {
      await api.clearSearchHistory('domain');
    } catch (error) {
      logger.warn('Failed to clear domain search history', error);
    }
  }, [userId]);

  const clearContactHistory = useCallback(async () => {
    if (!userId) return;

    setContactHistory([]);
    try {
      await api.clearSearchHistory('contact');
    } catch (error) {
      logger.warn('Failed to clear contact search history', error);
    }
  }, [userId]);

  return {
    domainHistory,
    contactHistory,
    addDomainSearch,
    addContactSearch,
    removeDomain,
    removeContact,
    clearDomainHistory,
    clearContactHistory,
  };
}
